import React from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProgressBar from '../components/ProgressBar';
import ScrollToTop from '../components/ScrollToTop';

const TermsAndConditions = () => {
  const { isDark } = useDarkMode();

  const sections = [
    {
      title: '1. Agreement to Terms',
      content: 'By accessing and using the Media Society website, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.'
    },
    {
      title: '2. Use License',
      content: 'Permission is granted to temporarily download one copy of the materials (information or software) on Media Society\'s website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not: modify or copy the materials; use the materials for any commercial purpose or for any public display; attempt to decompile or reverse engineer any software contained on the website; remove any copyright or other proprietary notations from the materials; or transfer the materials to another person or "mirror" the materials on any other server.'
    },
    {
      title: '3. Disclaimer of Warranties',
      content: 'The materials on Media Society\'s website are provided "as is". Media Society makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.'
    },
    {
      title: '4. Limitations of Liability',
      content: 'In no event shall Media Society or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on the Media Society website, even if Media Society or an authorized representative has been notified orally or in writing of the possibility of such damage.'
    },
    {
      title: '5. Accuracy of Materials',
      content: 'The materials appearing on Media Society\'s website could include technical, typographical, or photographic errors. Media Society does not warrant that any of the materials on the website are accurate, complete, or current. Media Society may make changes to the materials contained on the website at any time without notice.'
    },
    {
      title: '6. Links and Third-Party Services',
      content: 'Media Society has not reviewed all of the sites linked to our website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by Media Society of the site. Use of any such linked website is at the user\'s own risk.'
    },
    {
      title: '7. Modifications to Terms',
      content: 'Media Society may revise these terms of service at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of service.'
    },
    {
      title: '8. Intellectual Property Rights',
      content: 'All content on the Media Society website, including text, graphics, logos, images, and software, is the property of Media Society or its content suppliers and is protected by international copyright laws. Unauthorized use of any content is prohibited.'
    },
    {
      title: '9. Payment Terms and Policies',
      content: 'If you purchase any services from Media Society, you agree to pay all fees and charges that you incur. Media Society reserves the right to refuse service to anyone for any reason at any time. All services are provided based on agreed terms in separate service agreements.'
    },
    {
      title: '10. Governing Law',
      content: 'These terms and conditions are governed by and construed in accordance with the laws applicable in Pakistan, and you irrevocably submit to the exclusive jurisdiction of the courts in that location.'
    },
    {
      title: '11. Contact Information',
      content: 'If you have any questions about these Terms and Conditions, please contact us at: Email: ahtashamspeaks@gmail.com | Phone: 03047259307 | Address: Lahore, Pakistan'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className={`w-full transition-colors duration-500 ${isDark ? 'bg-black' : 'bg-light-secondary'}`}>
      <ProgressBar />
      <ScrollToTop />
      <Navbar />

      <section className={`w-full py-20 lg:py-32 transition-colors duration-500 ${isDark ? 'bg-black' : 'bg-light-secondary'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            className="mb-16"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: '-50px' }}
          >
            <motion.h1
              className={`font-montserrat font-bold text-4xl lg:text-5xl mb-4 transition-colors ${
                isDark ? 'text-white' : 'text-black'
              }`}
              variants={itemVariants}
            >
              Terms & Conditions
            </motion.h1>
            <motion.p
              className={`font-poppins text-lg transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
              variants={itemVariants}
            >
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </motion.p>
          </motion.div>

          {/* Content Sections */}
          <motion.div
            className="space-y-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: '-50px' }}
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`p-6 rounded-lg border transition-all duration-300 ${
                  isDark
                    ? 'bg-dark-secondary border-primary-gold/20 hover:border-primary-gold/40'
                    : 'bg-light-tertiary border-primary-gold/20 hover:border-primary-gold/40'
                }`}
              >
                <h2 className={`font-montserrat font-bold text-xl mb-3 transition-colors ${
                  isDark ? 'text-primary-gold' : 'text-primary-gold'
                }`}>
                  {section.title}
                </h2>
                <p className={`font-poppins leading-relaxed transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {section.content}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;
